import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, X, Check, Loader2, KeyRound, Edit2,
  ShieldCheck, Globe, UserCircle2, AlertCircle, ChevronLeft, ChevronRight,
  Smartphone, ShieldOff, QrCode, CheckCircle2,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth.store';
import { formatDate } from '../lib/formatters';
import { TableSkeleton } from '../components/TableSkeleton';

type AdminRole = 'CENTRAL_BANK_ADMIN' | 'WORLD_OWNER';

interface AdminUser {
  id: string;
  email: string;
  role: AdminRole;
  worldIds: string[];
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  totpEnabled: boolean;
}

const ROLES: { value: AdminRole; label: string; desc: string; color: string; icon: typeof ShieldCheck }[] = [
  { value: 'CENTRAL_BANK_ADMIN', label: 'Central Admin',  desc: 'Full platform access',      color: 'text-cyan-400',   icon: ShieldCheck },
  { value: 'WORLD_OWNER',        label: 'Partner Admin',  desc: 'Manages assigned worlds',   color: 'text-purple-400', icon: Globe },
];

const CREATABLE_ROLES = ROLES;

const ROLE_META = Object.fromEntries(ROLES.map((r) => [r.value, r])) as Record<AdminRole, typeof ROLES[number]>;

const inputCls = 'w-full bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/40 transition-colors placeholder-slate-700';

function RoleBadge({ role }: { role: string }) {
  const meta = ROLE_META[role as AdminRole] ?? { label: role, color: 'text-slate-400', icon: UserCircle2 };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full
      ${role === 'CENTRAL_BANK_ADMIN' ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-purple-500/10 border border-purple-500/20'
      } ${meta.color}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

// ─── Create User Modal ────────────────────────────────────────────────────────
function CreateUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<AdminRole>('WORLD_OWNER');
  const [worldId, setWorldId] = useState('');

  const { data: worlds = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (data: object) => api.post('/auth/admin-users', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      setEmail(''); setPassword(''); setRole('WORLD_OWNER'); setWorldId('');
      onClose();
    },
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(6,10,20,0.8)', backdropFilter: 'blur(6px)' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        className="glass-card w-full max-w-md p-6 space-y-5"
        style={{ borderColor: 'rgba(0,212,255,0.2)' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Plus className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-semibold text-white">Create User</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition-colors"><X className="w-4 h-4" /></button>
        </div>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate({ email, password, role, ...(worldId ? { worldId } : {}) });
          }}
        >
          <div>
            <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Email <span className="text-red-400 ml-0.5">*</span></label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} placeholder="admin@example.com" />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Password (min 12 chars) <span className="text-red-400 ml-0.5">*</span></label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={12} className={inputCls} placeholder="••••••••••••" />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-2">Role</label>
            <div className="grid grid-cols-2 gap-2">
              {CREATABLE_ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRole(r.value)}
                  className={`text-left px-3 py-2.5 rounded-xl border transition-all ${
                    role === r.value ? 'border-cyan-500/40 bg-cyan-500/8' : 'border-white/5 hover:border-white/10'
                  }`}
                >
                  <r.icon className={`w-3.5 h-3.5 mb-1 ${r.color}`} />
                  <div className={`text-[10px] font-semibold ${role === r.value ? 'text-white' : 'text-slate-400'}`}>{r.label}</div>
                  <div className="text-[9px] text-slate-600 mt-0.5">{r.desc}</div>
                </button>
              ))}
            </div>
          </div>
          {role === 'WORLD_OWNER' && (
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Assign World <span className="text-slate-700">(optional)</span></label>
              <select value={worldId} onChange={(e) => setWorldId(e.target.value)} className={inputCls}>
                <option value="">None</option>
                {worlds.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          )}

          {createMutation.isError && (
            <p className="text-red-400 text-xs flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" />
              {(createMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Create failed'}
            </p>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 text-xs text-slate-400 py-2.5 rounded-xl border border-white/8 hover:border-white/15 transition-all">Cancel</button>
            <button type="submit" disabled={createMutation.isPending} className="flex-1 btn-primary text-xs font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2">
              {createMutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating…</> : <><Check className="w-3.5 h-3.5" /> Create</>}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ─── Edit User Modal ───────────────────────────────────────────────────────────
function EditUserModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const qc = useQueryClient();
  const [role, setRole] = useState<AdminRole>(user.role);
  const [isActive, setIsActive] = useState(user.isActive);

  const { data: worlds = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  const [selectedWorldIds, setSelectedWorldIds] = useState<string[]>(user.worldIds);

  const editMutation = useMutation({
    mutationFn: (data: object) => api.patch(`/auth/admin-users/${user.id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-users'] }); onClose(); },
  });

  const toggleWorld = (id: string) =>
    setSelectedWorldIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(6,10,20,0.8)', backdropFilter: 'blur(6px)' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        className="glass-card w-full max-w-md p-6 space-y-5"
        style={{ borderColor: 'rgba(139,92,246,0.2)' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Edit2 className="w-4 h-4 text-purple-400" />
            <h3 className="text-sm font-semibold text-white">Edit User</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-slate-500 font-mono -mt-2">{user.email}</p>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            editMutation.mutate({ role, worldIds: selectedWorldIds, isActive });
          }}
        >
          <div>
            <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-2">Role</label>
            <div className="grid grid-cols-2 gap-2">
              {ROLES.map((r) => (
                <button key={r.value} type="button" onClick={() => setRole(r.value)}
                  className={`text-left px-3 py-2.5 rounded-xl border transition-all ${
                    role === r.value ? 'border-purple-500/40 bg-purple-500/8' : 'border-white/5 hover:border-white/10'
                  }`}
                >
                  <r.icon className={`w-3.5 h-3.5 mb-1 ${r.color}`} />
                  <div className={`text-[10px] font-semibold ${role === r.value ? 'text-white' : 'text-slate-400'}`}>{r.label}</div>
                </button>
              ))}
            </div>
          </div>

          {worlds.length > 0 && (
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-2">Assigned Worlds</label>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {worlds.map((w) => (
                  <label key={w.id} className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer border transition-all ${
                    selectedWorldIds.includes(w.id) ? 'border-purple-500/30 bg-purple-500/8' : 'border-white/5 hover:border-white/10'
                  }`}>
                    <input type="checkbox" checked={selectedWorldIds.includes(w.id)} onChange={() => toggleWorld(w.id)} className="w-3.5 h-3.5 accent-purple-500" />
                    <span className="text-xs text-slate-300">{w.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-white/5 bg-white/[0.02]">
            <span className="text-xs text-slate-400">Account active</span>
            <button
              type="button"
              onClick={() => setIsActive(!isActive)}
              className={`relative w-10 h-5 rounded-full transition-all ${isActive ? 'bg-emerald-500/60' : 'bg-slate-700'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${isActive ? 'left-5.5 left-[22px]' : 'left-0.5'}`} />
            </button>
          </div>

          {editMutation.isError && (
            <p className="text-red-400 text-xs flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" />
              {(editMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Update failed'}
            </p>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 text-xs text-slate-400 py-2.5 rounded-xl border border-white/8 hover:border-white/15 transition-all">Cancel</button>
            <button type="submit" disabled={editMutation.isPending} className="flex-1 text-xs font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 bg-purple-500/15 border border-purple-500/25 text-purple-300 hover:bg-purple-500/25 transition-all">
              {editMutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : <><Check className="w-3.5 h-3.5" /> Save Changes</>}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ─── Reset Password Modal ──────────────────────────────────────────────────────
function ResetPasswordModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const qc = useQueryClient();
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const resetMutation = useMutation({
    mutationFn: () => api.post(`/auth/admin-users/${user.id}/reset-password`, { newPassword }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-users'] }); onClose(); },
  });

  const mismatch = confirm.length > 0 && newPassword !== confirm;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(6,10,20,0.8)', backdropFilter: 'blur(6px)' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        className="glass-card w-full max-w-sm p-6 space-y-5"
        style={{ borderColor: 'rgba(245,158,11,0.2)' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-white">Reset Password</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-slate-500 font-mono -mt-2">{user.email}</p>
        <p className="text-xs text-amber-400/80">This will immediately invalidate all active sessions for this user.</p>

        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (!mismatch) resetMutation.mutate(); }}>
          <div>
            <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">New Password (min 12 chars) <span className="text-red-400 ml-0.5">*</span></label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={12} className={inputCls} placeholder="••••••••••••" />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Confirm Password <span className="text-red-400 ml-0.5">*</span></label>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className={`${inputCls} ${mismatch ? 'border-red-500/50' : ''}`} placeholder="••••••••••••" />
            {mismatch && <p className="text-red-400 text-[10px] mt-1">Passwords do not match</p>}
          </div>

          {resetMutation.isError && (
            <p className="text-red-400 text-xs flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" />
              {(resetMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Reset failed'}
            </p>
          )}
          {resetMutation.isSuccess && (
            <p className="text-emerald-400 text-xs flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Password reset successfully</p>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 text-xs text-slate-400 py-2.5 rounded-xl border border-white/8 hover:border-white/15 transition-all">Cancel</button>
            <button type="submit" disabled={resetMutation.isPending || mismatch} className="flex-1 text-xs font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 bg-amber-500/15 border border-amber-500/25 text-amber-300 hover:bg-amber-500/25 transition-all disabled:opacity-50">
              {resetMutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Resetting…</> : <><KeyRound className="w-3.5 h-3.5" /> Reset Password</>}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
// ─── MFA Settings Panel (self-service for logged-in user) ─────────────────────
type MfaPhase = 'idle' | 'setup-pending' | 'disable-confirm';

function MfaSettingsPanel({ currentUserEmail, users, onRefresh }: {
  currentUserEmail: string | null;
  users: AdminUser[];
  onRefresh: () => void;
}) {
  const currentUser = users.find((u) => u.email === currentUserEmail);
  const totpEnabled = currentUser?.totpEnabled ?? false;

  const [phase, setPhase] = useState<MfaPhase>('idle');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [rawSecret, setRawSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [statusMsg, setStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const setupMutation = useMutation({
    mutationFn: () => api.post('/auth/mfa/setup'),
    onSuccess: ({ data }) => {
      setQrDataUrl(data.qrCodeDataUrl);
      setRawSecret(data.secret);
      setPhase('setup-pending');
      setStatusMsg(null);
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      setStatusMsg({ type: 'error', text: e.response?.data?.message ?? 'Setup failed' });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (code: string) => api.post('/auth/mfa/verify', { code }),
    onSuccess: () => {
      setPhase('idle');
      setVerifyCode('');
      setQrDataUrl('');
      setRawSecret('');
      setStatusMsg({ type: 'success', text: 'MFA enabled — your account is now protected by TOTP.' });
      onRefresh();
    },
    onError: () => setStatusMsg({ type: 'error', text: 'Invalid code — please try again.' }),
  });

  const disableMutation = useMutation({
    mutationFn: (code: string) => api.delete('/auth/mfa', { data: { code } }),
    onSuccess: () => {
      setPhase('idle');
      setDisableCode('');
      setStatusMsg({ type: 'success', text: 'MFA disabled.' });
      onRefresh();
    },
    onError: () => setStatusMsg({ type: 'error', text: 'Invalid code — MFA was not disabled.' }),
  });

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${
          totpEnabled
            ? 'bg-emerald-500/10 border-emerald-500/20'
            : 'bg-slate-500/10 border-slate-500/20'
        }`}>
          <Smartphone className={`w-5 h-5 ${totpEnabled ? 'text-emerald-400' : 'text-slate-500'}`} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white">Two-Factor Authentication</h3>
          <p className="text-xs text-slate-600">
            {totpEnabled
              ? 'TOTP is active — your account requires a code on every login.'
              : 'Add a second factor to protect your account.'}
          </p>
        </div>
        <div className="ml-auto">
          {totpEnabled ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
              <CheckCircle2 className="w-3 h-3" /> Enabled
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full bg-slate-500/10 border border-slate-500/20 text-slate-500">
              <ShieldOff className="w-3 h-3" /> Disabled
            </span>
          )}
        </div>
      </div>

      {/* Status message */}
      <AnimatePresence>
        {statusMsg && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`flex items-center gap-2 text-xs rounded-xl px-3 py-2.5 mb-4 ${
              statusMsg.type === 'success'
                ? 'bg-emerald-500/8 border border-emerald-500/20 text-emerald-400'
                : 'bg-red-500/8 border border-red-500/20 text-red-400'
            }`}
          >
            {statusMsg.type === 'success' ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />}
            {statusMsg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Idle: action buttons */}
      {phase === 'idle' && (
        <div className="flex gap-3">
          {!totpEnabled && (
            <button
              onClick={() => { setStatusMsg(null); setupMutation.mutate(); }}
              disabled={setupMutation.isPending}
              className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-all disabled:opacity-50"
            >
              {setupMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <QrCode className="w-3.5 h-3.5" />}
              Enable MFA
            </button>
          )}
          {totpEnabled && (
            <button
              onClick={() => { setStatusMsg(null); setPhase('disable-confirm'); }}
              className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 hover:bg-rose-500/20 transition-all"
            >
              <ShieldOff className="w-3.5 h-3.5" /> Disable MFA
            </button>
          )}
        </div>
      )}

      {/* Setup pending: show QR + verify */}
      {phase === 'setup-pending' && (
        <div className="space-y-4">
          <div className="flex gap-5 items-start">
            {qrDataUrl && (
              <img src={qrDataUrl} alt="TOTP QR code" className="w-36 h-36 rounded-xl border border-white/10 flex-shrink-0" />
            )}
            <div className="flex-1 space-y-2">
              <p className="text-xs text-slate-400">
                Scan the QR code with <span className="text-white font-medium">Google Authenticator</span>, <span className="text-white font-medium">Authy</span>, or any TOTP app.
              </p>
              <p className="text-[10px] text-slate-600">Or enter the secret manually:</p>
              <code className="block text-[11px] font-mono text-cyan-300 bg-cyan-500/5 border border-cyan-500/10 rounded-lg px-3 py-2 tracking-wider break-all">
                {rawSecret}
              </code>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">
              Enter code to confirm
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                maxLength={7}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/[^0-9 ]/g, ''))}
                autoFocus
                className="flex-1 bg-[#0b1120] border border-white/10 rounded-xl px-4 py-2.5 text-xl font-mono tracking-[0.3em] text-center text-white focus:outline-none focus:border-emerald-500/40 transition-colors placeholder-slate-700"
                placeholder="000000"
              />
              <button
                onClick={() => verifyMutation.mutate(verifyCode)}
                disabled={verifyMutation.isPending || verifyCode.replace(/\s/g, '').length < 6}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-semibold bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-all disabled:opacity-50"
              >
                {verifyMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Activate
              </button>
              <button
                onClick={() => { setPhase('idle'); setQrDataUrl(''); setRawSecret(''); setVerifyCode(''); }}
                className="px-3 py-2.5 rounded-xl text-xs text-slate-500 hover:text-slate-300 border border-white/8 hover:border-white/15 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disable confirm: enter code to disable */}
      {phase === 'disable-confirm' && (
        <div className="space-y-3">
          <p className="text-xs text-slate-400">Enter your current TOTP code to confirm disabling MFA:</p>
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              maxLength={7}
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/[^0-9 ]/g, ''))}
              autoFocus
              className="flex-1 bg-[#0b1120] border border-white/10 rounded-xl px-4 py-2.5 text-xl font-mono tracking-[0.3em] text-center text-white focus:outline-none focus:border-rose-500/40 transition-colors placeholder-slate-700"
              placeholder="000000"
            />
            <button
              onClick={() => disableMutation.mutate(disableCode)}
              disabled={disableMutation.isPending || disableCode.replace(/\s/g, '').length < 6}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-semibold bg-rose-500/10 border border-rose-500/20 text-rose-400 hover:bg-rose-500/20 transition-all disabled:opacity-50"
            >
              {disableMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldOff className="w-3.5 h-3.5" />}
              Confirm
            </button>
            <button
              onClick={() => { setPhase('idle'); setDisableCode(''); }}
              className="px-3 py-2.5 rounded-xl text-xs text-slate-500 hover:text-slate-300 border border-white/8 hover:border-white/15 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export function UsersPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [resetUser, setResetUser] = useState<AdminUser | null>(null);
  const role = useAuthStore((s) => s.role);
  const currentEmail = useAuthStore((s) => s.email);
  const isReadOnly = role === 'ADMIN_READONLY';

  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery<AdminUser[]>({
    queryKey: ['admin-users'],
    queryFn: () => api.get('/auth/admin-users').then((r) => r.data),
  });

  const USERS_PER_PAGE = 25;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(users.length / USERS_PER_PAGE));
  const paginatedUsers = useMemo(
    () => users.slice((page - 1) * USERS_PER_PAGE, page * USERS_PER_PAGE),
    [users, page],
  );

  return (
    <div className="p-8 space-y-6">
      {/* Modals */}
      <AnimatePresence>
        {showCreate && <CreateUserModal open={showCreate} onClose={() => setShowCreate(false)} />}
        {editingUser && <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} />}
        {resetUser && <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/15 to-purple-500/15 border border-cyan-500/20 flex items-center justify-center">
            <Users className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">User Management</h2>
            <p className="text-xs text-slate-600 mt-0.5">{users.length} admin account{users.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        {!isReadOnly && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-xl btn-primary"
          >
            <Plus className="w-3.5 h-3.5" /> New User
          </button>
        )}
      </div>

      {/* Role legend */}
      <div className="grid grid-cols-3 gap-3">
        {ROLES.map((r) => (
          <div key={r.value} className="glass-card p-4 flex items-start gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
              ${r.value === 'CENTRAL_BANK_ADMIN' ? 'bg-cyan-500/10 border border-cyan-500/20' :
                r.value === 'WORLD_OWNER' ? 'bg-purple-500/10 border border-purple-500/20' :
                'bg-slate-500/10 border border-slate-500/20'}`}>
              <r.icon className={`w-4 h-4 ${r.color}`} />
            </div>
            <div>
              <p className={`text-xs font-semibold ${r.color}`}>{r.label}</p>
              <p className="text-[10px] text-slate-600 mt-0.5">{r.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Users table */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card overflow-hidden"
      >
        <div className="h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                {['Email', 'Role', 'Worlds', 'Status', 'MFA', 'Created', 'Last Login', ''].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-[10px] font-semibold uppercase tracking-widest text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableSkeleton rows={5} cols={8} />
              ) : users.length === 0 ? (
                <tr><td colSpan={8} className="px-5 py-12 text-center text-slate-600">No users yet</td></tr>
              ) : paginatedUsers.map((u) => (
                <tr key={u.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }} className="table-row-hover">
                  <td className="px-5 py-3 text-slate-200 font-medium">{u.email}</td>
                  <td className="px-5 py-3"><RoleBadge role={u.role} /></td>
                  <td className="px-5 py-3 text-slate-500">
                    {u.worldIds.length > 0 ? (
                      <span className="font-mono text-slate-400">{u.worldIds.length} world{u.worldIds.length !== 1 ? 's' : ''}</span>
                    ) : (
                      <span className="text-slate-700">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-full font-medium ${
                      u.isActive
                        ? 'bg-emerald-500/15 border border-emerald-500/25 text-emerald-400'
                        : 'bg-slate-500/15 border border-slate-500/20 text-slate-500'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.isActive ? 'bg-emerald-400 pulse-dot' : 'bg-slate-600'}`} />
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {u.totpEnabled ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400">
                        <Smartphone className="w-3 h-3" /> On
                      </span>
                    ) : (
                      <span className="text-slate-700 text-[10px]">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-600">
                    {formatDate(u.createdAt)}
                  </td>
                  <td className="px-5 py-3 text-slate-600">
                    {u.lastLoginAt ? formatDate(u.lastLoginAt) : <span className="text-slate-700">Never</span>}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      {!isReadOnly && (
                        <button
                          onClick={() => setEditingUser(u)}
                          className="text-[10px] font-medium text-slate-400 hover:text-white px-2.5 py-1 rounded-lg border border-white/8 hover:border-white/20 transition-all flex items-center gap-1"
                        >
                          <Edit2 className="w-3 h-3" /> Edit
                        </button>
                      )}
                      {!isReadOnly && (
                        <button
                          onClick={() => setResetUser(u)}
                          className="text-[10px] font-medium text-amber-400 hover:text-amber-300 px-2.5 py-1 rounded-lg border border-amber-500/20 hover:bg-amber-500/10 transition-all flex items-center gap-1"
                        >
                          <KeyRound className="w-3 h-3" /> Reset PW
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-white/[0.04]">
            <span className="text-xs text-slate-600">
              Page {page} of {totalPages} &bull; {users.length} users
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 border border-white/[0.06] disabled:opacity-30 hover:border-white/10 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 border border-white/[0.06] disabled:opacity-30 hover:border-white/10 transition-colors"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* MFA self-service panel */}
      <MfaSettingsPanel
        currentUserEmail={currentEmail}
        users={users}
        onRefresh={() => qc.invalidateQueries({ queryKey: ['admin-users'] })}
      />
    </div>
  );
}
